import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR, priceLabel } from './lib';
import { AlternativePicker, RichText, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock, SectionsBlock, TotalsBlock, QuoteMetaBlock } from './SharedBlocks';

const PremiumLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied,
  } = ctx;

  const cust = quote.customerData || {};
  const sans = "'Inter', system-ui, sans-serif";

  const Row = ({ service, color, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '18px 0', borderBottom: `1px solid ${theme.surfaceBorder}`, opacity: isOpt && !on ? 0.5 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {isOpt && (
                <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)}
                  style={{ width: 18, height: 18, accentColor: theme.primary, cursor: 'pointer' }} />
              )}
              <p style={{ fontSize: 18, fontWeight: 600, color: theme.textPrimary, margin: 0, fontFamily: sans }}>{service.name}</p>
            </div>
            {service.description && <p style={{ fontSize: 15, color: theme.textSecondary, marginTop: 8, lineHeight: 1.75, fontFamily: sans }}>{service.description}</p>}
            <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: sans }}>
            <span style={{ fontSize: 18.5, fontWeight: 600, color: theme.textPrimary }}>{priceLabel(service, lineTotal(service))}</span>
            {suffix && <div style={{ fontSize: 12.5, color: theme.textMuted }}>{suffix}</div>}
          </div>
        </div>
      </div>
    );
  };

  const Group = ({ title, services, color, suffix }) => {
    if (!services.length) return null;
    return (
      <div style={{ marginTop: 8 }}>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: theme.textPrimary, fontFamily: sans, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
          {title}
        </h3>
        {services.map((s, i) => <Row key={s.id || i} service={s} color={color} suffix={suffix} />)}
      </div>
    );
  };

  const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: theme.radius, background: 'transparent', border: `1px solid ${theme.surfaceBorder}`, color: theme.textSecondary, cursor: 'pointer', fontSize: 13, fontFamily: sans };

  return (
    // Serif (theme.font) nur für Headlines — Fließtext läuft in Inter,
    // sonst wird die komplette Seite zur Zierschrift (User-Feedback).
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: sans, color: theme.textPrimary, padding: '0 0 64px' }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '18px 20px', maxWidth: 960, margin: '0 auto' }}>
        <button onClick={handleSaveAsPdf} style={btn}><Download className="w-4 h-4" />PDF</button>
        <button onClick={handleCopyLink} style={btn}>{linkCopied ? <Check className="w-4 h-4" style={{ color: '#22c55e' }} /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}</button>
      </div>

      {/* Bildschirm: 960px breit; beim Druck zieht .quote-shell in print.css auf ~A4-Breite zusammen */}
      <div className="quote-shell" style={{ maxWidth: 960, margin: '0 auto', padding: '0 32px' }}>

        {/* HERO */}
        <div style={{ padding: '56px 0 40px' }}>
          {(() => {
            const src = (u) => (u && u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
            // Dunkles Layout + weiße Logo-Variante vorhanden → direkt auf den
            // Hintergrund, ohne weißen Chip. Druck (helles Theme) nimmt das
            // farbige Logo. Chip bleibt Fallback ohne logoLight.
            if (theme.dark && company?.logoLight) {
              return <img src={src(company.logoLight)} alt="Logo" style={{ height: 52, objectFit: 'contain', display: 'block', marginBottom: 40 }} />;
            }
            if (company?.logo) {
              return (
                <span style={theme.dark ? { display: 'inline-block', background: '#ffffff', borderRadius: 10, padding: '8px 14px', marginBottom: 40 } : { marginBottom: 40, display: 'inline-block' }}>
                  <img src={src(company.logo)} alt="Logo" style={{ height: 36, objectFit: 'contain', display: 'block' }} />
                </span>
              );
            }
            return <p style={{ fontSize: 12, letterSpacing: 3, color: theme.textMuted, fontFamily: sans, marginBottom: 40 }}>{(company?.name || '').toUpperCase()}</p>;
          })()}
          {cust.company && <p style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: theme.textMuted, fontFamily: sans, marginBottom: 18 }}>Für {cust.company}</p>}
          <h1 style={{ fontSize: 46, lineHeight: 1.22, fontWeight: 500, margin: 0, color: theme.textPrimary, fontFamily: theme.font }}>
            {quote.heroHeadline || quote.quoteTitle || 'Ein Angebot, das überzeugt.'}
          </h1>
          {quote.quoteIntro && <RichText value={quote.quoteIntro} style={{ fontSize: 17.5, color: theme.textSecondary, marginTop: 22, lineHeight: 1.8, fontFamily: sans}} />}
        </div>

        {quote.videoUrl && (
          <div className="no-print" style={{ marginBottom: 40 }}>
            <video src={quote.videoUrl} controls style={{ width: '100%', borderRadius: theme.radius, maxHeight: 440 }} />
          </div>
        )}

        {quote.customerArguments && (
          <div style={{ borderTop: `1px solid ${theme.surfaceBorder}`, borderBottom: `1px solid ${theme.surfaceBorder}`, padding: '28px 0', margin: '8px 0 24px' }}>
            <div style={{ fontSize: 17, color: theme.textSecondary, lineHeight: 1.8, fontFamily: sans }} dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
          </div>
        )}

        <QuoteMetaBlock quote={quote} theme={theme} company={company} />
        <Group title="Einmalige Leistungen" services={einmaligServices} color={theme.einmalig} suffix="" />
        <Group title="Monatliche Leistungen" services={monatlichServices} color={theme.monatlich} suffix="/ Monat" />
        <Group title="Jährliche Leistungen" services={jaehrlichServices} color={theme.jaehrlich} suffix="/ Jahr" />

        {/* Price statement */}
        <div style={{ padding: '44px 0 36px', textAlign: 'center' }}>
          <p style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: theme.textMuted, fontFamily: sans, marginBottom: 10 }}>Investition gesamt</p>
          <p style={{ fontSize: 'clamp(34px, 9vw, 52px)', fontWeight: 500, color: theme.primary, margin: 0, lineHeight: 1, fontFamily: theme.font }}>{formatEUR(einmaligTotals.netto)}</p>
          {(monatlichTotals.netto > 0 || jaehrlichTotals.netto > 0) && (
            <p style={{ fontSize: 15, color: theme.textSecondary, marginTop: 12, fontFamily: sans }}>
              {monatlichTotals.netto > 0 && <>zzgl. {formatEUR(monatlichTotals.netto)} / Monat </>}
              {jaehrlichTotals.netto > 0 && <>· {formatEUR(jaehrlichTotals.netto)} / Jahr</>}
            </p>
          )}
          <p style={{ fontSize: 12, color: theme.textMuted, marginTop: 8, fontFamily: sans }}>netto · zzgl. 19 % MwSt.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <TermsBlock quote={quote} company={company} theme={theme} />
          <TotalsBlock ctx={ctx} theme={theme} />
          <SectionsBlock quote={quote} theme={theme} ctx={ctx} />
          <AcceptanceBlock ctx={ctx} theme={theme} />
          <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FeedbackBlock viewHash={viewHash} theme={theme} />
            <QuestionBlock viewHash={viewHash} theme={theme} />
          </div>
          <FooterBlock company={company} theme={theme} />
        </div>
      </div>
    </div>
  );
};

export default PremiumLayout;
